import { api } from "@/lib/api";

const RZP_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";
const FIRST_KEY = "wh_first_letter_paid";

export const PRICE_INR = {
  first: 19,
  next: 29,
};

/** Has the user already paid for a letter on this device? */
export function isFirstLetter() {
  try {
    return !localStorage.getItem(FIRST_KEY);
  } catch {
    return true;
  }
}

/** Mark that at least one letter has been paid for on this device. */
export function markFirstLetterUsed() {
  try {
    localStorage.setItem(FIRST_KEY, new Date().toISOString());
  } catch {
    /* localStorage might be blocked — safe to ignore */
  }
}

export function currentLetterPriceInr() {
  return isFirstLetter() ? PRICE_INR.first : PRICE_INR.next;
}

export function currentLetterKind() {
  return isFirstLetter() ? "first" : "next";
}

let scriptPromise = null;
export function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = RZP_SCRIPT_URL;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => { scriptPromise = null; reject(new Error("Razorpay script failed to load")); };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export async function createOrder(kind) {
  try {
    const { data } = await api.post("/create-order", { kind });
    return data;
  } catch (e) {
    const d = e?.response?.data?.detail;
    const msg =
      typeof d === "string"
        ? d
        : d?.message ||
          d?.razorpay_body ||
          e?.message ||
          "Could not create payment order";
    const err = new Error(msg);
    err.response = e?.response?.data;
    throw err;
  }
}

export async function verifyPaymentAndCreate(payment, letterPayload) {
  const { data } = await api.post("/verify-payment", {
    razorpay_payment_id: payment.razorpay_payment_id,
    razorpay_order_id: payment.razorpay_order_id,
    razorpay_signature: payment.razorpay_signature,
    letter: letterPayload,
  });
  return data;
}

/**
 * Full checkout flow: create order → open Razorpay → verify → create letter.
 * Marks the device as "first letter used" only after a successful create.
 */
export async function runCheckout({ letterPayload, prefill = {}, theme = {} }) {
  await loadRazorpay();
  const kind = currentLetterKind();
  const order = await createOrder(kind);

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "WrittenHearts",
      description:
        kind === "first"
          ? "Your first letter · ₹19"
          : "A new letter · ₹29",
      order_id: order.order_id,
      prefill,
      theme: { color: "#991B1B", ...theme },
      notes: { app: "writtenhearts", kind },
      modal: {
        ondismiss: () => reject(new Error("cancelled")),
        escape: true,
      },
      handler: async (response) => {
        try {
          const res = await verifyPaymentAndCreate(response, letterPayload);
          markFirstLetterUsed();
          resolve(res);
        } catch (e) {
          reject(e);
        }
      },
    });
    rzp.on("payment.failed", (resp) => {
      reject(new Error(resp?.error?.description || "Payment failed"));
    });
    rzp.open();
  });
}
